/* eslint-disable camelcase, max-classes-per-file */
import { loginService, APIBase } from '@agro1desenvolvimento/apis-js-package';
import dayjs from 'dayjs';
import { ENV } from '../../utilities/constants';
import { convertApiDate } from '../../utilities/date';
import imagensAPI, { ImagemAPI } from './imagensAPI';
import { AnaliseStatus } from '../../@types/sementes/analiseStatus';

class LaboratorioAPI extends APIBase {
  constructor() {
    super();

    let baseURL = 'https://homologacao-integrations.aqila.com.br';
    if (ENV === 'production') {
      baseURL = 'https://integrations.aqila.com.br';
    }

    this.initialize({ baseURL });

    this.request.interceptors.request.use((request) => {
      const headers = loginService.requestHeaders();
      delete headers['X-Scope'];
      request.headers = {
        ...headers,
        ...request.headers,
        'X-Scope': 'sementes',
      };
      return request;
    });
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const laboratorioAPI = new LaboratorioAPI();

/**
 * Converte uma data URL (base64) para Blob
 */
function dataURLtoBlob(dataURL: string): Blob {
  const arr = dataURL.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(arr[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);

  for (let i = 0; i < n; i += 1) {
    u8arr[i] = bstr.charCodeAt(i);
  }

  return new Blob([u8arr], { type: mime });
}

interface APIEndereco {
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
}

interface APIColeta {
  id: string;
  numero_remessa: string;
  data_entrada: string;
  data_amostra: string;
  data_prazo_entrega: string;
  safra: string;
  nome_cientifico?: string;
  atividade?: string;
}

interface APIAmostra {
  id_amostra: number;
  cultivar: string;
  nome_cientifico: string;
  cultivar_longo: string;
  categoria: string;
  especie: string;
  numero_lote: string;
  numero_campo: string;
  segregacao: number;
  peneira: string;
  unidade: string;
  unidade_sigla: string;
  quantidade: number;
  quantidade_convertida: number;
  quantidade_campo_producao: number;
  percentual_peneira: number;
  percentual_descarte: number;
  umidade: number;
  referencia_laboratorio: string;
  aspecto_visual: string;
  linha: string;
  beneficiada: boolean;
  tratada: boolean;
  campo: boolean;
  calda: string;
  peso_amostra: number;
  numero_amostra: string;
  observacao: string;
  numero_romaneio: number;
}

interface APIDeterminacao {
  id: string;
  ordem: number;
  descricao: string;
  descricao_reduzida: string;
  abreviatura: string;
  integration_status: number;
}

interface APIRequerente {
  id: string;
  nome: string;
  nome_fantasia: string;
  documento: string;
  inscricao: string;
  telefone: string;
  email: string;
  endereco?: APIEndereco;
}

interface APIAnalista {
  id: string;
  nome: string;
  nome_fantasia: string;
  documento: string;
  inscricao: string;
  telefone: string;
  email: string;
  endereco?: APIEndereco;
}

interface APIProdutor {
  id: string;
  nome: string;
  nome_fantasia: string;
  documento: string;
}

interface APIDano {
  id: string;
  intenso: boolean;
}

interface APIClassificacaoSemente {
  id: number;
  contagem: string;
  grau: string;
  danos: APIDano[];
}

interface APIRepeticao {
  repeticao: string;
  classificacoes_sementes?: APIClassificacaoSemente[];
  classificaoes_sementes?: APIClassificacaoSemente[]; // typo da API
}

interface APIProgressoRepeticao {
  repeticao: string;
  progresso: string;
}

interface APIResultadosCAN {
  amostras: number[];
  media: number;
}

interface APIImagemLaboratorio {
  erp_id: string;
  name: string;
  size: number;
  url?: string; // URL pré-assinada, quando disponível (legado)
  urls?: {
    thumbnail: string;
    fullsize: string;
  };
}

export interface APIAnaliseLaboratorio {
  id: string;
  coleta: APIColeta;
  amostra: APIAmostra;
  determinacao: APIDeterminacao;
  requerente: APIRequerente;
  analista?: APIAnalista;
  produtor?: APIProdutor;
  status: 'N' | 'I' | 'C';
  reteste: boolean;
  observacao: string;
  data_instalacao: string;
  data_analise: string;
  updated_at: string;
  integration_status: number;
  integration_id?: string;
  model: string;
  deleted: string | null;
  created_at: string;
  numero_repeticoes?: number;
  sementes_repeticao?: number;
  solucao?: number;
  gerar_bas_rir?: boolean;
  repeticao_rejeitada?: string;
  progresso_repeticoes?: APIProgressoRepeticao[];
  resultados?: APIRepeticao[] | APIResultadosCAN;
  imagens?: APIImagemLaboratorio[];
  concluido_api?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface APIListResponse {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  data: APIAnaliseLaboratorio[];
}

export const StatusAPIMap = {
  N: 'Pendente',
  I: 'Iniciado',
  C: 'Realizado',
} as const;

export interface TetrazolioItem {
  id: string;
  produtor: string;
  nAmostra: string;
  especie: string;
  dataEntrada: Date | null;
  dataAnalise?: Date;
  status: string;
  progresso: string;
  repeticaoRejeitada?: number;
}

export interface TetrazolioListParams {
  produtorId: string;
  page?: number;
  perPage?: number;
  search?: string;
  status?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  dataInicio?: string;
  dataFim?: string;
  determinacao?: 'TZ' | 'CAN';
}

export interface TetrazolioListResponse {
  data: TetrazolioItem[];
  meta: {
    currentPage: number;
    perPage: number;
    totalItems: number;
    totalPages: number;
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function convertAPIToInternal(apiData: APIAnaliseLaboratorio): TetrazolioItem {
  // Formatar progresso baseado em progresso_repeticoes
  // Formato: R1 e R3 na primeira linha, R2 e R4 na segunda (ímpares/pares)
  let progressoFormatado = '--';
  if (apiData.progresso_repeticoes && apiData.progresso_repeticoes.length > 0) {
    const impares: string[] = [];
    const pares: string[] = [];

    apiData.progresso_repeticoes.forEach(({ repeticao, progresso }) => {
      const texto = `R${repeticao}: ${progresso || '0'}`;
      if (Number(repeticao) % 2 === 1) {
        impares.push(texto);
      } else {
        pares.push(texto);
      }
    });

    const linhas = [];
    if (impares.length > 0) linhas.push(impares.join(' '));
    if (pares.length > 0) linhas.push(pares.join(' '));
    progressoFormatado = linhas.join('\n');
  }

  return {
    id: apiData.id,
    produtor: apiData.requerente.nome,
    nAmostra: apiData.amostra.numero_amostra
      || (apiData.amostra.id_amostra ? String(apiData.amostra.id_amostra) : null)
      || '--',
    especie: apiData.coleta.atividade ?? apiData.amostra.especie ?? '--',
    dataEntrada: convertApiDate(apiData.coleta.data_entrada),
    dataAnalise: apiData.data_analise ? dayjs(apiData.data_analise).toDate() : undefined,
    status: StatusAPIMap[apiData.status],
    progresso: progressoFormatado,
    repeticaoRejeitada:
      apiData.repeticao_rejeitada ? Number(apiData.repeticao_rejeitada) : undefined,
  };
}

export interface ImagemTetrazolio {
  id: string;
  url: string; // thumbnail para exibição em lista
  fullsizeUrl?: string; // imagem completa para modal
  nome: string;
  tipo: 'upload' | 'camera';
  timestamp: Date;
}

export interface LancamentoSementeAPI {
  contagem: number;
  grau: string;
  danos: Array<{
    tipo: string;
    intensidade: string;
  }>;
}

export interface TetrazolioDetalhes {
  id: string;
  produtor: string;
  idDaAmostra: string;
  numeroRemessa: string;
  especie: string;
  lote: string;
  safra: string;
  nomeCientifico: string;
  cultivar: string;
  categoria: string;
  dataEntrada: Date | null;
  dataAmostragem: Date | null;
  status: AnaliseStatus;
  concluidoApi?: boolean;
  observacao?: string;
  dadosAnaliseInicial?: {
    analistaId: string;
    data: Date;
    solucao: string;
    repeticoes: number;
    sementesPorRepeticao: number;
    gerarBASRIR: boolean;
    repeticaoRejeitada: number | null;
    observacao?: string;
  };
  imagens?: ImagemTetrazolio[];
  lancamentosPorRepeticao?: Record<number, LancamentoSementeAPI[]>;
}

export interface ClassificacaoSemente {
  grau: number;
  danos: Array<{
    tipo: string;
    intensidade: string;
  }>;
}

class TetrazolioService {
  endpoint() {
    return `/${loginService.scope}/sementes/laboratorio/tetrazolio`;
  }

  async sincronizarClassificacoes(
    id: string,
    classificacoes: ClassificacaoSemente[],
  ): Promise<void> {
    await laboratorioAPI.put(
      `/api/v1/agro1/laboratorio/analises_laboratorios/${id}`,
      {
        payload: {
          message: {
            classificacoes,
          },
        },
      },
    );
  }

  async buscarImagens(id: string): Promise<ImagemTetrazolio[]> {
    try {
      const imagensAPI_result = await imagensAPI.listarImagens(id);

      return imagensAPI_result.map((img: ImagemAPI) => ({
        id: img.erp_id,
        // Prioriza thumbnail da nova estrutura, depois URL legado, por último constrói manualmente
        url: img.urls?.thumbnail || img.url || imagensAPI.buildS3Url(img.file),
        fullsizeUrl: img.urls?.fullsize || img.url || imagensAPI.buildS3Url(img.file),
        nome: img.name,
        tipo: 'upload' as const,
        timestamp: new Date(img.created_at),
      }));
    } catch (error) {
      console.error('Erro ao buscar imagens:', error);
      return [];
    }
  }

  async adicionarImagem(
    id: string,
    imagem: Omit<ImagemTetrazolio, 'id'>,
  ): Promise<ImagemTetrazolio> {
    try {
      // Converter data URL para Blob
      const blob = dataURLtoBlob(imagem.url);
      const contentType = blob.type;

      // Obter URL pré-assinada
      const presignedData = await imagensAPI.getPresignedUrl(
        id,
        imagem.nome,
        contentType,
      );

      // Upload para S3
      await imagensAPI.uploadToS3(presignedData.url, blob, contentType);

      // Retornar imagem com o ID do registro criado pela API
      return {
        id: presignedData.id, // Usar o ID do registro retornado pela API
        url: imagem.url, // Manter URL local por enquanto
        nome: presignedData.filename,
        tipo: imagem.tipo,
        timestamp: imagem.timestamp,
      };
    } catch (error) {
      console.error('Erro ao adicionar imagem:', error);
      throw error;
    }
  }

  async removerImagem(imagemId: string): Promise<void> {
    try {
      await imagensAPI.removerImagem(imagemId);
    } catch (error) {
      console.error('Erro ao remover imagem:', error);
      throw error;
    }
  }

  async list(params: TetrazolioListParams): Promise<TetrazolioListResponse> {
    const {
      page = 1, produtorId, determinacao, search,
    } = params;
    const queryParams: any = {
      page,
      produtor_id: produtorId,
      determinacao,
    };

    if (search) {
      queryParams.search_term = search;
    }

    if (params.status) {
      const statusMap: Record<string, string> = {
        Pendente: 'pendente',
        Iniciado: 'iniciado',
        Realizado: 'realizado',
      };
      const statusArray = params.status.split(',').map((s) => statusMap[s] || s);
      queryParams.status = statusArray.join(',');
    }

    if (params.dataInicio) {
      queryParams.data_inicio = params.dataInicio;
    }

    if (params.dataFim) {
      queryParams.data_fim = params.dataFim;
    }

    if (params.orderBy) {
      queryParams.order_by = params.orderBy;
    }

    if (params.orderDirection) {
      queryParams.order_direction = params.orderDirection;
    }

    const response = await laboratorioAPI.get<APIListResponse>(
      '/api/v1/agro1/laboratorio/analises_laboratorios',
      { params: queryParams },
    );

    const convertedData = response.data.data.map(convertAPIToInternal);

    return {
      data: convertedData,
      meta: {
        currentPage: response.data.page,
        perPage: response.data.per_page,
        totalItems: response.data.total,
        totalPages: response.data.total_pages,
      },
    };
  }

  async getById(id: string, produtorId: string): Promise<TetrazolioDetalhes> {
    const response = await laboratorioAPI.get<APIListResponse>(
      '/api/v1/agro1/laboratorio/analises_laboratorios',
      { params: { id, produtor_id: produtorId } },
    );

    if (!response.data.data || response.data.data.length === 0) {
      throw new Error(`Análise de tetrazólio com ID ${id} não encontrada`);
    }

    const apiData = response.data.data[0];
    const detalhes: TetrazolioDetalhes = {
      id: apiData.id,
      produtor: apiData.requerente.nome,
      idDaAmostra: apiData.amostra.numero_amostra
        || (apiData.amostra.id_amostra ? String(apiData.amostra.id_amostra) : null)
        || '--',
      numeroRemessa: apiData.coleta.numero_remessa || '--',
      especie: apiData.coleta.atividade || apiData.amostra.especie || apiData.amostra.cultivar,
      lote: apiData.amostra.numero_lote,
      safra: apiData.coleta.safra,
      nomeCientifico: apiData.coleta.nome_cientifico || apiData.amostra.nome_cientifico || '--',
      cultivar: apiData.amostra.cultivar_longo || apiData.amostra.cultivar,
      categoria: apiData.amostra.categoria,
      dataEntrada: convertApiDate(apiData.coleta.data_entrada),
      dataAmostragem: convertApiDate(apiData.coleta.data_amostra),
      status: StatusAPIMap[apiData.status],
      concluidoApi: apiData.concluido_api || false,
      observacao: apiData.observacao || undefined,
    };

    // Adicionar imagens da resposta da API
    if (apiData.imagens && apiData.imagens.length > 0) {
      detalhes.imagens = apiData.imagens.map((img) => ({
        id: img.erp_id,
        // Prioriza thumbnail da nova estrutura, depois URL legado
        url: img.urls?.thumbnail || img.url || '',
        fullsizeUrl: img.urls?.fullsize || img.url || '',
        nome: img.name,
        tipo: 'upload' as const,
        timestamp: new Date(),
      }));
    }

    // Recuperar lançamentos de repetições já realizadas
    const lancamentosPorRepeticao: Record<number, LancamentoSementeAPI[]> = {};
    let maiorNumeroRepeticao = 0;

    if (apiData.resultados && Array.isArray(apiData.resultados)) {
      apiData.resultados.forEach((repeticao) => {
        const numRepeticao = Number(repeticao.repeticao);
        maiorNumeroRepeticao = Math.max(maiorNumeroRepeticao, numRepeticao);

        // API pode retornar com typo ou correto
        const sementes = repeticao.classificacoes_sementes
          || repeticao.classificaoes_sementes || [];
        lancamentosPorRepeticao[numRepeticao] = sementes.map((sem) => ({
          contagem: Number(sem.contagem),
          grau: sem.grau,
          danos: sem.danos.map((dano) => ({
            tipo: dano.id,
            intensidade: dano.intenso ? 'intenso' : 'normal',
          })),
        }));
      });

      detalhes.lancamentosPorRepeticao = lancamentosPorRepeticao;
    }

    if (apiData.data_analise && apiData.numero_repeticoes && apiData.sementes_repeticao) {
      // Usar o maior entre numero_repeticoes da API e o maior encontrado nos resultados
      // Isso garante que se a API não atualizou numero_repeticoes, ainda pegaremos o correto
      const totalRepeticoes = Math.max(
        Number(apiData.numero_repeticoes),
        maiorNumeroRepeticao,
      );

      detalhes.dadosAnaliseInicial = {
        analistaId: apiData.analista?.id || '',
        data: dayjs(apiData.data_analise).toDate(),
        solucao: apiData.solucao ? String(apiData.solucao) : '0.5',
        repeticoes: totalRepeticoes,
        sementesPorRepeticao: Number(apiData.sementes_repeticao),
        gerarBASRIR: apiData.gerar_bas_rir ?? false,
        repeticaoRejeitada:
          apiData.repeticao_rejeitada ? Number(apiData.repeticao_rejeitada) : null,
        observacao: apiData.observacao || undefined,
      };
    }

    return detalhes;
  }

  async updateAnaliseInicial(
    id: string,
    dados: {
      analistaId: string;
      data: Date;
      solucao: string;
      repeticoes: number;
      sementesPorRepeticao: number;
      gerarBASRIR: boolean;
      observacao?: string;
    },
  ): Promise<void> {
    await laboratorioAPI.put(
      `/api/v1/agro1/laboratorio/analises_laboratorios/${id}`,
      {
        payload: {
          message: {
            analista: { id: dados.analistaId },
            data_analise: dados.data.toISOString().split('T')[0],
            solucao: parseFloat(dados.solucao.replace(',', '.')),
            numero_repeticoes: String(dados.repeticoes),
            sementes_repeticao: String(dados.sementesPorRepeticao),
            gerar_bas_rir: dados.gerarBASRIR,
            observacao: dados.observacao || '',
          },
        },
      },
    );
  }

  async submitResultados(
    id: string,
    lancamentosPorRepeticao: Record<number, LancamentoSementeAPI[]>,
    concluir = false,
  ): Promise<void> {
    const resultados = Object.entries(lancamentosPorRepeticao).map(([repeticao, lancamentos]) => ({
      repeticao: String(repeticao),
      classificacoes_sementes: lancamentos.map((lancamento) => ({
        contagem: String(lancamento.contagem),
        grau: String(lancamento.grau),
        danos: lancamento.danos.map((dano) => ({
          id: dano.tipo,
          intenso: dano.intensidade === 'intenso',
        })),
      })),
    }));

    await laboratorioAPI.put(
      `/api/v1/agro1/laboratorio/analises_laboratorios/resultados/${id}`,
      {
        payload: {
          message: {
            resultados,
          },
          concluido_api: concluir,
        },
      },
    );
  }

  async updateRepeticaoRejeitada(
    id: string,
    numeroRepeticaoRejeitada: number | null,
    totalRepeticoes?: number,
  ): Promise<void> {
    const message: any = {
      repeticao_rejeitada: numeroRepeticaoRejeitada !== null
        ? String(numeroRepeticaoRejeitada)
        : null,
    };

    // Se aumentou o número de repetições, atualizar também
    if (totalRepeticoes !== undefined) {
      message.numero_repeticoes = String(totalRepeticoes);
    }

    await laboratorioAPI.put(
      `/api/v1/agro1/laboratorio/analises_laboratorios/${id}`,
      {
        payload: {
          message,
        },
      },
    );
  }

  async cancelarAnalise(id: string): Promise<void> {
    await laboratorioAPI.put(
      `/api/v1/agro1/laboratorio/analises_laboratorios/${id}/cancel`,
      {
        payload: {
          message: {},
        },
      },
    );
  }

  async concluirAnalise(id: string): Promise<void> {
    await laboratorioAPI.put(
      `/api/v1/agro1/laboratorio/analises_laboratorios/${id}/complete`,
    );
  }
}

export default new TetrazolioService();
